/**
 * Order detail drawer (§18 /queue/:orderId). Read-only container: fetches the
 * order detail and renders it in the shared <Drawer/> (scrim, Esc, focus trap).
 * The header carries the order # + status + meta; the scrolling body is the
 * presentational <OrderDetailBody/> (routing trace §8.5, readiness §9, address,
 * lines, shipments). No actions in this increment.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, type OrderDetail } from "../lib/api.js";
import { Drawer, Skeleton, ErrorState, Button } from "../components/ui/index.js";
import { isPackable } from "../lib/status.js";
import { OrderTitle, OrderMeta, OrderDetailBody } from "./queue/OrderDetailView.js";

export function OrderDrawer() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    api
      .getOrder(orderId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn’t load this order.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const close = () => navigate("/queue");

  return (
    <Drawer
      onClose={close}
      ariaLabel={detail ? `Order ${detail.order.name}` : "Order detail"}
      title={detail ? <OrderTitle order={detail.order} /> : <h1 className="mono">{orderId}</h1>}
      meta={detail ? <OrderMeta order={detail.order} /> : undefined}
    >
      {loading ? (
        <div className="skeleton-stack">
          <Skeleton width="70%" height="var(--sp-4)" />
          <Skeleton width="40%" />
          <Skeleton width="90%" height="var(--sp-16)" />
          <Skeleton width="80%" height="var(--sp-16)" />
        </div>
      ) : error ? (
        <ErrorState title="Couldn’t load this order" onRetry={() => navigate(`/queue/${orderId}`, { replace: true })} />
      ) : !detail ? (
        <p className="muted">Order not found.</p>
      ) : (
        <>
          {/* Task 1 quick-pack — straight to the bench for this one order. */}
          {isPackable(detail.order.status) && (
            <div className="od-pack-bar">
              <Button
                variant="primary"
                icon="package"
                block
                onClick={() => navigate(`/pack?order=${encodeURIComponent(detail.order.id)}`)}
              >
                Pack this order
              </Button>
            </div>
          )}
          <OrderDetailBody detail={detail} />
        </>
      )}
    </Drawer>
  );
}
